import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
    LayoutDashboard, MessageSquare, FileText, Settings, Stethoscope, Power, Users, Clock,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { DashSidebar } from "../components/DashSidebar";
import { MobileBottomNav } from "../components/MobileBottomNav";
import { ConsultationChat } from "../components/ConsultationChat";
import { DoctorRequestAccessPanel } from "../components/DoctorRequestAccessPanel";

const ITEMS = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "consultations", label: "Consultations", icon: MessageSquare },
    { id: "patients", label: "Patients", icon: Users },
    { id: "settings", label: "Settings", icon: Settings },
];

export default function DoctorDashboard() {
    const { user, setUser } = useAuth();
    const { hash } = useLocation();
    const tab = (hash || "#overview").replace("#", "");

    const [consults, setConsults] = useState([]);
    const [active, setActive] = useState(null);
    const [online, setOnline] = useState(user?.online ?? false);
    const [updating, setUpdating] = useState(false);

    const loadConsults = async () => {
        try {
            const { data } = await api.get("/consultations");
            setConsults(data);
            // refresh active consultation full data
            if (active) {
                const fresh = await api.get(`/consultations/${active.id}`);
                setActive(fresh.data);
            }
        } catch (e) {/* silent */ void e; }
    };

    useEffect(() => { loadConsults(); const t = setInterval(loadConsults, 5000); return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleOnline = async () => {
        setUpdating(true);
        try {
            const { data } = await api.patch("/doctors/me/status", { online: !online });
            setOnline(data.online);
            setUser((u) => ({ ...u, online: data.online }));
            toast.success(data.online ? "You're now online" : "You're offline");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not update status");
        } finally { setUpdating(false); }
    };

    const openConsult = async (c) => {
        try {
            const { data } = await api.get(`/consultations/${c.id}`);
            setActive(data);
        } catch (e) {
            toast.error("Could not open consult");
        }
    };

    const stats = {
        total: consults.length,
        active: consults.filter((c) => c.status === "active").length,
        granted: consults.filter((c) => c.vitals_access_granted).length,
    };

    return (
        <div className="flex bg-[var(--medx-bg-soft)] min-h-screen pb-20 md:pb-0" data-testid="doctor-dashboard">
            <div className="hidden md:block">
                <DashSidebar items={ITEMS} basePath="/doctor" title="Doctor" />
            </div>
            <main className="flex-1 p-4 md:p-10 min-w-0">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    <div>
                        <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-1">DOCTOR PORTAL</div>
                        <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight">{user?.name}</h1>
                        <div className="text-sm text-gray-500">{user?.speciality} · {user?.hospital || "Independent"}</div>
                    </div>
                    <button onClick={toggleOnline} disabled={updating}
                        className={`px-5 py-3 rounded-full font-semibold text-sm flex items-center gap-2 shadow-lg transition-all ${
                            online ? "bg-green-500 hover:bg-green-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                        }`}
                        data-testid="doctor-online-toggle">
                        <Power className="w-4 h-4" /> {updating ? "…" : (online ? "Online · accepting consults" : "Go online")}
                    </button>
                </div>

                {tab === "overview" && (
                    <div className="space-y-6" data-testid="doctor-tab-overview">
                        <div className="grid grid-cols-3 gap-3 md:gap-5">
                            <div className="card-soft p-5"><div className="text-xs font-mono text-gray-500">CONSULTS</div><div className="font-display text-3xl font-bold">{stats.total}</div></div>
                            <div className="card-soft p-5"><div className="text-xs font-mono text-gray-500">ACTIVE</div><div className="font-display text-3xl font-bold">{stats.active}</div></div>
                            <div className="card-soft p-5"><div className="text-xs font-mono text-gray-500">VITALS UNLOCKED</div><div className="font-display text-3xl font-bold">{stats.granted}</div></div>
                        </div>

                        <DoctorRequestAccessPanel
                            onConsultationUnlocked={async (cid) => {
                                // Open the unlocked consultation automatically the first time we see it
                                if (active?.id === cid) return;
                                try {
                                    const { data } = await api.get(`/consultations/${cid}`);
                                    setActive(data);
                                } catch { /* silent */ }
                            }}
                        />

                        <div className="card-soft p-6">
                            <div className="text-xs font-mono tracking-wider text-gray-500 mb-3">PENDING / RECENT CONSULTATIONS</div>
                            {consults.length === 0 ? (
                                <div className="text-center text-sm text-gray-500 py-6">No consults yet. Patients will reach out once you're online.</div>
                            ) : (
                                <div className="space-y-2">
                                    {consults.slice(0, 8).map((c) => (
                                        <button key={c.id} onClick={() => openConsult(c)}
                                            className="w-full flex items-center justify-between gap-3 p-3 rounded-xl border border-gray-100 hover:border-[var(--medx-orange)] hover:bg-orange-50/30 transition-all text-left"
                                            data-testid={`doctor-consult-row-${c.id}`}>
                                            <div className="min-w-0">
                                                <div className="font-medium text-gray-900">{c.patient_name}</div>
                                                <div className="text-xs text-gray-500 truncate max-w-md">{c.problem}</div>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <div className="text-xs flex items-center gap-1 text-gray-400 justify-end">
                                                    <Clock className="w-3 h-3" /> {new Date(c.created_at).toLocaleDateString()}
                                                </div>
                                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono ${c.vitals_access_granted ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                                                    {c.vitals_access_granted ? "VITALS UNLOCKED" : "PIN REQUIRED"}
                                                </span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {tab === "consultations" && (
                    <div className="space-y-6" data-testid="doctor-tab-consultations">
                        <h2 className="font-display text-2xl font-semibold">All Consultations</h2>
                        <div className="card-soft overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 text-gray-500 text-xs font-mono tracking-wider">
                                    <tr>
                                        <th className="text-left p-4">PATIENT</th>
                                        <th className="text-left p-4">PROBLEM</th>
                                        <th className="text-left p-4">STATUS</th>
                                        <th className="text-left p-4">VITALS</th>
                                        <th className="text-left p-4">DATE</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {consults.length === 0 && (<tr><td colSpan={5} className="p-8 text-center text-gray-500">No consultations yet.</td></tr>)}
                                    {consults.map((c) => (
                                        <tr key={c.id} className="border-t border-gray-100 hover:bg-orange-50/40 cursor-pointer" onClick={() => openConsult(c)}>
                                            <td className="p-4 font-medium">{c.patient_name}</td>
                                            <td className="p-4 text-gray-600 truncate max-w-xs">{c.problem}</td>
                                            <td className="p-4"><span className="text-xs px-2 py-1 rounded-full bg-orange-50 text-[var(--medx-red)]">{c.status}</span></td>
                                            <td className="p-4">{c.vitals_access_granted ? "✓ unlocked" : "—"}</td>
                                            <td className="p-4 text-xs text-gray-500">{new Date(c.created_at).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {tab === "patients" && (
                    <div className="space-y-6" data-testid="doctor-tab-patients">
                        <h2 className="font-display text-2xl font-semibold">My Patients</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[...new Map(consults.filter((c) => c.vitals_access_granted).map((c) => [c.patient_id, c])).values()].map((c) => (
                                <button key={c.patient_id} onClick={() => openConsult(c)} className="card-soft p-5 text-left hover:-translate-y-0.5 transition-all">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)] grid place-items-center text-white font-bold">
                                            {(c.patient_name || "?").slice(0, 1).toUpperCase()}
                                        </div>
                                        <div>
                                            <div className="font-display font-semibold">{c.patient_name}</div>
                                            <div className="text-xs text-gray-500">Last consult {new Date(c.created_at).toLocaleDateString()}</div>
                                        </div>
                                    </div>
                                </button>
                            ))}
                            {consults.filter((c) => c.vitals_access_granted).length === 0 && (
                                <div className="card-soft p-8 text-center text-sm text-gray-500 md:col-span-2">No patient files unlocked yet — open a consult and enter the patient's PIN.</div>
                            )}
                        </div>
                    </div>
                )}

                {tab === "settings" && (
                    <div className="space-y-6" data-testid="doctor-tab-settings">
                        <h2 className="font-display text-2xl font-semibold">Profile</h2>
                        <div className="card-soft p-6 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div><div className="text-xs text-gray-500">NAME</div><div className="font-medium">{user?.name}</div></div>
                            <div><div className="text-xs text-gray-500">SPECIALITY</div><div className="font-medium">{user?.speciality}</div></div>
                            <div><div className="text-xs text-gray-500">LICENSE</div><div className="font-medium font-mono">{user?.license_number}</div></div>
                            <div><div className="text-xs text-gray-500">HOSPITAL</div><div className="font-medium">{user?.hospital || "—"}</div></div>
                            <div className="md:col-span-2"><div className="text-xs text-gray-500">BIO</div><div>{user?.bio || "—"}</div></div>
                        </div>
                    </div>
                )}
            </main>
            <MobileBottomNav items={ITEMS} basePath="/doctor" />
            {active && (
                <ConsultationChat
                    consultation={active}
                    currentRole="doctor"
                    onClose={() => { setActive(null); loadConsults(); }}
                    onConsultationUpdated={async () => {
                        const { data } = await api.get(`/consultations/${active.id}`);
                        setActive(data);
                        loadConsults();
                    }}
                />
            )}
        </div>
    );
}
